import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { Box, FormControlLabel, Paper, Typography } from '@material-ui/core';
import { OnboardingStepTitle } from '@vyce/core/src/components';
import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { getFormFoldersRequest } from '@vyce/core/src/api/forms';
import { FormFolderDoc } from '@vyce/core/src/api/types';
import { FolderAutocomplete } from './FolderAutocomplete';

const FORM_FOLDERS_FETCH_LIMIT = 500;

export const FormSettingsStep: React.FC<{
  companyId?: string;
  userId?: string;
  showClockInBlock?: boolean;
}> = ({ companyId, userId, showClockInBlock = true }) => {
  const { control, setValue, getValues } = useFormContext();
  const { handleServerError } = useContext(NotificationContext);
  const [levels, setLevels] = useState<FormFolderDoc[][]>([]);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const didBootstrapRef = useRef(false);

  const loadFoldersByParent = useCallback(
    async (parentId: string | null): Promise<FormFolderDoc[]> => {
      if (!companyId || !userId) return [];
      const { folders } = await getFormFoldersRequest({
        companyId,
        userId,
        parentId,
        limit: FORM_FOLDERS_FETCH_LIMIT,
      });
      return folders;
    },
    [companyId, userId]
  );

  useEffect(() => {
    if (!companyId || !userId) return;
    if (didBootstrapRef.current) return;

    let cancelled = false;

    const sortByName = (xs: FormFolderDoc[]) =>
      [...xs].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
      );

    const bootstrap = async () => {
      try {
        setLoadingFolders(true);
        const seededFolderId = getValues('folderId') as string | null | undefined;

        if (!seededFolderId) {
          const rootFolders = await loadFoldersByParent(null);
          if (cancelled) return;
          setLevels([rootFolders]);
          didBootstrapRef.current = true;
          return;
        }

        const { folders: allFolders } = await getFormFoldersRequest({
          companyId,
          userId,
          limit: FORM_FOLDERS_FETCH_LIMIT,
        });
        if (cancelled) return;

        const filterByParent = (parentId: string | null) =>
          sortByName(
            allFolders.filter(f => (f.parentId ?? null) === parentId && f.archived !== true)
          );

        const target = allFolders.find(f => f.id === seededFolderId);
        if (!target) {
          setLevels([filterByParent(null)]);
          didBootstrapRef.current = true;
          return;
        }

        const ancestorIds = Array.isArray(target.path) ? target.path : [];
        const chainIds = [...ancestorIds, target.id];
        const newLevels: FormFolderDoc[][] = [filterByParent(null)];
        for (let i = 0; i < chainIds.length - 1; i++) {
          newLevels.push(filterByParent(chainIds[i]));
        }
        const targetChildren = filterByParent(target.id);
        if (targetChildren.length) {
          newLevels.push(targetChildren);
        }

        setLevels(newLevels);
        setSelectedPath(chainIds);
        didBootstrapRef.current = true;
      } catch (error) {
        if (cancelled) return;
        handleServerError(error);
        setLevels([[]]);
        didBootstrapRef.current = true;
      } finally {
        if (!cancelled) setLoadingFolders(false);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [companyId, getValues, handleServerError, loadFoldersByParent, userId]);

  const handleChangeLevel = async (levelIndex: number, folderId: string) => {
    const nextPath = [...selectedPath.slice(0, levelIndex), folderId];
    setSelectedPath(nextPath);
    setLevels(prev => prev.slice(0, levelIndex + 1));
    setValue('folderId', folderId || null);

    if (!folderId) return;

    try {
      setLoadingFolders(true);
      const subfolders = await loadFoldersByParent(folderId);
      if (subfolders.length) {
        setLevels(prev => {
          const nextLevels = prev.slice(0, levelIndex + 1);
          nextLevels.push(subfolders);
          return nextLevels;
        });
      }
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoadingFolders(false);
    }
  };

  const levelLabels = useMemo(
    () =>
      levels.map((_, index) => {
        if (index === 0) return 'Folder';
        if (index === 1) return 'Subfolder';
        return `Subfolder ${index}`;
      }),
    [levels]
  );

  return (
    <Paper variant="outlined" style={{ padding: 24 }}>
      <OnboardingStepTitle text="Lastly, additional settings:" />

      <Box marginTop={2}>
        <Controller
          control={control}
          name="signature"
          render={({ field }) => (
            <FormControlLabel
              control={<AppCheckbox color="primary" checked={field.value} onChange={field.onChange} />}
              label="Require user's signature at the end of the form"
            />
          )}
        />
      </Box>

      {showClockInBlock && (
        <Box marginTop={2}>
          <Controller
            control={control}
            name="clockInBlock"
            render={({ field }) => (
              <FormControlLabel
                style={{ alignItems: 'flex-start' }}
                control={<AppCheckbox color="primary" checked={field.value} onChange={field.onChange} />}
                label={
                  <Box display="flex" flexDirection="column" marginTop={1}>
                    <span>Block the recipient's Clock In option until the form is completed</span>
                    <Typography variant="caption" color="textSecondary">
                      Note this only blocks team members from clocking in that are using the Vyce Time
                      &amp; Attendance app and associated to the locations you’ve chosen.
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>
      )}

      {!!companyId && !!userId && (
        <Box marginTop={2} display="flex" flexDirection="column" gridGap={8}>
          {levels.map((foldersAtLevel, levelIndex) => (
            <FolderAutocomplete
              key={`folder-level-${levelIndex}`}
              folders={foldersAtLevel}
              selectedFolderId={selectedPath[levelIndex] || ''}
              onFolderChange={folderId => {
                void handleChangeLevel(levelIndex, folderId);
              }}
              loading={loadingFolders}
              label={levelLabels[levelIndex]}
            />
          ))}
          {!loadingFolders && levels[0]?.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No available folders
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};
